/**
 * ThumbnailStrip — horizontal document switcher under the stage: one small
 * image button per available document, active one ringed in Tinta.
 */

import { useState } from "react";
import { Box, ButtonBase, Stack, Typography } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";

import type { DocumentRef } from "../../api/client";
import { isPdfDocument } from "./DocumentStage";
import { labelForTipo } from "./docLabels";

function Thumb({
  doc,
  active,
  onSelect,
}: {
  doc: DocumentRef;
  active: boolean;
  onSelect: () => void;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <ButtonBase
      onClick={onSelect}
      aria-pressed={active}
      aria-label={labelForTipo(doc.tipo)}
      sx={{
        flexDirection: "column",
        gap: 0.5,
        p: 0.75,
        borderRadius: 1,
        border: "2px solid",
        borderColor: active ? "primary.main" : "transparent",
        "&:hover": { bgcolor: "action.hover" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.light",
          outlineOffset: 1,
        },
      }}
    >
      {failed || isPdfDocument(doc) ? (
        // PDFs never go through <img> (it can't decode them); they get the
        // same placeholder tile up front, with a PDF-specific icon.
        <Box
          sx={{
            width: 64,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "grey.100",
            borderRadius: 0.5,
            color: "text.disabled",
          }}
        >
          {isPdfDocument(doc) ? (
            <PictureAsPdfOutlinedIcon fontSize="small" />
          ) : (
            <InsertDriveFileOutlinedIcon fontSize="small" />
          )}
        </Box>
      ) : (
        <Box
          component="img"
          src={doc.url}
          alt=""
          onError={() => setFailed(true)}
          sx={{
            width: 64,
            height: 64,
            objectFit: "cover",
            borderRadius: 0.5,
            bgcolor: "grey.100",
            display: "block",
          }}
        />
      )}
      <Typography
        variant="caption"
        sx={{
          color: active ? "primary.main" : "text.secondary",
          fontWeight: active ? 600 : 400,
          lineHeight: 1.2,
        }}
      >
        {labelForTipo(doc.tipo)}
      </Typography>
    </ButtonBase>
  );
}

export function ThumbnailStrip({
  documents,
  activeTipo,
  onSelect,
}: {
  documents: DocumentRef[];
  activeTipo: DocumentRef["tipo"];
  onSelect: (tipo: DocumentRef["tipo"]) => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ mt: 1.5, flexWrap: "wrap", justifyContent: "center" }}
      useFlexGap
    >
      {documents.map((doc) => (
        <Thumb
          key={`${doc.object_name}:${doc.url}`}
          doc={doc}
          active={doc.tipo === activeTipo}
          onSelect={() => onSelect(doc.tipo)}
        />
      ))}
    </Stack>
  );
}
